import { AgentAvatarInput } from "~/pages/manifold-nextgen/components/agents/AgentAvatar";
import { Agent } from "~/services/agents";

export interface AgentSlot {
  kind: "agent";
  agent: AgentAvatarInput;
}

export interface UnavailableAgentSlot {
  kind: "unavailable";
  id: string;
}

export type SessionAgentSlot = AgentSlot | UnavailableAgentSlot;

export const UNAVAILABLE_AGENT_LABEL = "Agent unavailable";

/** Resolves a session's served agent ids against the project roster, in
 * first-appearance order. An id on the roster becomes an agent slot; an id
 * that isn't (removed from the project, deleted, or not viewable — the
 * roster read already omits the last two) becomes an unavailable slot, so the
 * caller renders the generic icon in its place instead of dropping it.
 * `undefined` while the roster itself is still loading, so callers can tell
 * that apart from a session with no agents at all. A roster read that failed
 * comes in as an empty roster (`sessionAgentRoster` in `ProjectLayout`), so
 * every id becomes an unavailable slot instead of loading forever. */
export function sessionAgentSlots(
  agentIds: readonly (string | null | undefined)[],
  roster: Agent[] | undefined
): SessionAgentSlot[] | undefined {
  if (roster === undefined) {
    return undefined;
  }

  const rosterById = new Map(roster.map((agent) => [agent.id, agent]));
  const seen = new Set<string>();
  const slots: SessionAgentSlot[] = [];

  for (const id of agentIds) {
    if (id == null || seen.has(id)) {
      continue;
    }
    seen.add(id);
    const agent = rosterById.get(id);
    slots.push(agent ? { kind: "agent" as const, agent } : { kind: "unavailable" as const, id });
  }

  return slots;
}
